import { SubAccountPermissionItem, SubAccountPermissionSection } from 'vopay';
import { PermissionItem, PermissionSection } from 'shared/permissions';

// Adapts `GET /account/subaccount/permissions` — an arbitrarily-nested tree of `SubAccountPermissionSection`
// — into shared/permissions' normalized model, for the sub-account row's Permissions drawer on the partner
// account page (ID-13430). The account page's own Permissions tab reuses it: `GET /partner/account/permissions`
// with `Version=new` (ID-13428, earthnode ID-13687) is normalized by vopay to the same tree. The same adaptation client-dashboard keeps beside its Sub-Account List page: per
// packages/shared/CLAUDE.md a port never pulls host-domain code into shared, so each host carries its own
// copy of the raw shape's adapter and the shared drawer only ever sees `PermissionSection[]`.
//
// The one non-trivial piece is the dollar-under-toggle pairing: a non-toggle field is drawn under a toggle
// when the field's raw `Section` equals the toggle's raw `Label`. That match MUST run on raw values, before
// `label` is localized — otherwise the toggle's label would be French while the child's `Section` is still
// English and the match would silently break. So the pairing is computed here on the raw item, and the
// matched fields become the toggle's `children`; they are not also left as top-level siblings.

/**
 * How the host turns raw `code`/`section` identifiers into localized display strings. Keying stays on the
 * raw identifiers; only the rendered label changes. Both default to identity (the raw value); the drawer
 * passes `usePermissionLabels`.
 */
export type PermissionLabelResolvers = {
  resolveItemLabel?: (code: string, rawLabel: string) => string;
  resolveSectionLabel?: (sectionID: string, rawLabel: string) => string;
};

/** Section IDs drawn as one always-open card rather than a collapsible. */
const ALWAYS_OPEN_SECTION_IDS = ['global-velocities'];

/** Account `isPermissionArray`: a leaf holds items, a branch holds sub-sections. Empty counts as a leaf. */
function isItemArray(
  subItems: SubAccountPermissionItem[] | SubAccountPermissionSection[],
): subItems is SubAccountPermissionItem[] {
  return subItems.length === 0 || 'Permission' in subItems[0];
}

function toItem(
  raw: SubAccountPermissionItem,
  children: PermissionItem[],
  resolvers: PermissionLabelResolvers,
): PermissionItem {
  return {
    code: raw.Permission,
    type: raw.Type,
    value: raw.Value,
    label: resolvers.resolveItemLabel?.(raw.Permission, raw.Label) ?? raw.Label,
    dependencies: raw.Dependencies ?? [],
    isCurrency: raw.IsCurrency ?? false,
    children,
  };
}

/** Build one leaf section's items, pairing each toggle with its dollar/text children on raw values. */
function buildLeafItems(rawItems: SubAccountPermissionItem[], resolvers: PermissionLabelResolvers): PermissionItem[] {
  const claimed = new Set<string>();
  const items: PermissionItem[] = [];

  // Toggles first, each carrying the non-toggle fields whose raw `Section` matches its raw `Label`.
  rawItems.forEach((raw) => {
    if (raw.Type !== 'boolean') return;

    const rawChildren = rawItems.filter((child) => child.Type !== 'boolean' && child.Section === raw.Label);
    rawChildren.forEach((child) => claimed.add(child.Permission));

    items.push(
      toItem(
        raw,
        rawChildren.map((child) => toItem(child, [], resolvers)),
        resolvers,
      ),
    );
  });

  // Non-toggle fields not paired under any toggle stay as top-level items (nothing is dropped).
  rawItems.forEach((raw) => {
    if (raw.Type !== 'boolean' && !claimed.has(raw.Permission)) {
      items.push(toItem(raw, [], resolvers));
    }
  });

  return items;
}

function adaptSection(section: SubAccountPermissionSection, resolvers: PermissionLabelResolvers): PermissionSection {
  const label = resolvers.resolveSectionLabel?.(section.SectionId, section.SectionName) ?? section.SectionName;

  if (isItemArray(section.SubItems)) {
    return {
      id: section.SectionId,
      label,
      items: buildLeafItems(section.SubItems, resolvers),
      sections: [],
      // Keyed on the ID, not the English `SectionName`, so it still matches once the label is localized.
      alwaysOpen: ALWAYS_OPEN_SECTION_IDS.includes(section.SectionId),
    };
  }

  return {
    id: section.SectionId,
    label,
    items: [],
    sections: section.SubItems.map((subSection) => adaptSection(subSection, resolvers)),
  };
}

export default function adaptSubAccountPermissions(
  sections: SubAccountPermissionSection[],
  resolvers: PermissionLabelResolvers = {},
): PermissionSection[] {
  return sections.map((section) => adaptSection(section, resolvers));
}
